/**
 * WISHLIST_REGENERATE_FIGURE — Phase 16 frontend.
 *
 * Modal dialog that kicks off the regenerate-from-image flow on a
 * single drawing figure. The drafter:
 *   1. Sees the current figure thumbnail + source-quality badge.
 *   2. Picks a drawing style (schematic / isometric / exploded).
 *   3. Optionally types a steering hint (drafter notes).
 *   4. Clicks "Generate 3 candidates".
 *
 * The endpoint returns a job_id; we register it with the drawing
 * store via setGenerationJob() so the existing GenerateAiModal
 * picks up the poll cycle and opens automatically when the job
 * completes. From the drafter's perspective, regenerated
 * candidates render through the same gallery + selection UX as
 * Generate-AI candidates — exactly what the brief asks for under
 * "Candidate review integration".
 *
 * Out of scope for Phase 16 (deferred follow-up):
 *   - Live Tier 1 element preview (would need a streaming /
 *     intermediate-state endpoint).
 *   - "Restore original source image" overflow menu item (needs a
 *     separate backend endpoint to swap raster back).
 *   - Differentiating regenerated candidates from AI-generated
 *     ones in the gallery header ("Based on original FIG. X" copy).
 */
import React, { useCallback, useState } from 'react';
import { Loader2, Sparkles, X } from 'lucide-react';
import { useDrawingStore } from '@/store/drawingStore';
import {
  postRegenerateFigureFromImage,
  type DrawingFigureSummary,
} from '@/api/client';

type RegenStyle =
  | 'match_source'
  | 'schematic'
  | 'isometric'
  | 'exploded';

const _STYLE_OPTIONS: { value: RegenStyle; label: string; hint: string }[] = [
  { value: 'match_source',
    label: 'Match source',
    hint: 'Reproduce in the same style as the original drawing.' },
  { value: 'schematic',
    label: 'Schematic',
    hint: 'Block-and-line abstraction (boxes + connectors).' },
  { value: 'isometric',
    label: 'Isometric',
    hint: '30°/30° orthographic projection for 3D structure.' },
  { value: 'exploded',
    label: 'Exploded',
    hint: 'Elements pulled apart along the dominant axis.' },
];

interface Props {
  matterId: string;
  figure: DrawingFigureSummary;
  onClose: () => void;
}

export function RegenerateFigurePanel({
  matterId, figure, onClose,
}: Props): JSX.Element {
  const [style, setStyle] = useState<RegenStyle>('match_source');
  const [drafterIntent, setDrafterIntent] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const setGenerationJob = useDrawingStore((s) => s.setGenerationJob);

  const onSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await postRegenerateFigureFromImage(matterId, figure.id, {
        style,
        drafter_intent: drafterIntent.trim(),
      });
      // Hand off to the existing generation-job polling cycle. The
      // GenerateAiModal listens on activeGenerationJobId; the
      // existing completed-flip-to-modal behaviour (drawingStore
      // line ~533) opens the candidate gallery automatically.
      setGenerationJob(matterId, res.job_id, {
        job_id:       res.job_id,
        status:       'queued',
        progress:     { step: 'queued', percent: 0 },
        candidates:   [],
        consumed_candidate_temp_ids: [],
        error:        null,
        created_at:   null,
        completed_at: null,
      });
      onClose();
    } catch (e: unknown) {
      const errResp = (e as {
        response?: { data?: { detail?: unknown; error_code?: string } };
      })?.response;
      const detail = errResp?.data?.detail as string | undefined;
      const message = detail
        || (e as { message?: string })?.message
        || 'Could not start regeneration.';
      setSubmitError(String(message));
      setSubmitting(false);
    }
  }, [
    matterId, figure.id, style, drafterIntent, submitting,
    setGenerationJob, onClose,
  ]);

  const sourceQuality = figure.source_quality || 'unknown';
  const qualityChipTone =
    sourceQuality === 'clean'
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
      : sourceQuality === 'unknown'
        ? 'bg-zinc-100 text-zinc-600 ring-zinc-200'
        : 'bg-amber-50 text-amber-700 ring-amber-200';

  const intentLength = drafterIntent.length;
  const intentTooLong = intentLength > 1000;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Regenerate figure from image"
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40"
      data-testid="regenerate-figure-panel"
    >
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-md bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-zinc-200 px-5 py-3">
          <div>
            <h2 className="text-base font-semibold text-zinc-900">
              Regenerate {figure.fig_label || 'figure'}
            </h2>
            <p className="mt-0.5 text-xs text-zinc-500">
              Clean up a low-quality source image into a USPTO-compliant
              patent line drawing. The original is preserved automatically.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-sm p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 disabled:opacity-50"
            aria-label="Close"
            data-testid="regenerate-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 space-y-4 overflow-y-auto px-5 py-4">
          {/* Source preview + quality */}
          <div className="flex items-start gap-3">
            <div className="flex h-24 w-24 flex-shrink-0 items-center justify-center overflow-hidden rounded-sm border border-zinc-200 bg-zinc-50">
              <img
                src={`${figure.thumb_url}?_v=${
                  figure.updated_at ? Date.parse(figure.updated_at) || 0 : 0
                }`}
                alt={figure.fig_label || ''}
                className="h-full w-full object-contain"
                loading="lazy"
              />
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-zinc-900">
                  Source quality
                </span>
                <span
                  className={`inline-flex shrink-0 items-center rounded-sm px-1.5 py-0.5 text-[10px] font-medium ring-1 ${qualityChipTone}`}
                  data-testid="source-quality-chip"
                >
                  {sourceQuality}
                </span>
              </div>
              <p className="text-[12px] leading-snug text-zinc-600">
                {sourceQuality === 'color_photo'
                  ? 'Source is a colour photograph; regenerate as a patent line drawing.'
                  : sourceQuality === 'screenshot'
                    ? 'Source appears to be a screenshot; regenerate as a patent line drawing.'
                    : sourceQuality === 'sketch'
                      ? 'Source is a hand sketch; regenerate as a clean patent line drawing.'
                      : sourceQuality === 'clean'
                        ? 'Source is already clean; regeneration will produce stylistic variants.'
                        : 'Source quality unknown; the vision pass will classify it during regeneration.'}
              </p>
            </div>
          </div>

          {/* Style selector */}
          <div>
            <div className="mb-1.5 text-xs font-medium text-zinc-700">
              Drawing style
            </div>
            <div
              className="grid grid-cols-2 md:grid-cols-4 gap-2"
              role="radiogroup"
              aria-label="Drawing style"
            >
              {_STYLE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={style === opt.value}
                  onClick={() => setStyle(opt.value)}
                  disabled={submitting}
                  data-testid={`regenerate-style-${opt.value}`}
                  className={[
                    'flex flex-col items-start rounded-sm border px-3 py-2 text-left',
                    style === opt.value
                      ? 'border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7]'
                      : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                  ].join(' ')}
                >
                  <span className="text-xs font-medium">{opt.label}</span>
                  <span className="mt-0.5 text-[10px] leading-tight text-zinc-500">
                    {opt.hint}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Drafter notes */}
          <div>
            <label
              htmlFor="regenerate-drafter-intent"
              className="mb-1.5 block text-xs font-medium text-zinc-700"
            >
              Drafter notes (optional)
            </label>
            <textarea
              id="regenerate-drafter-intent"
              value={drafterIntent}
              onChange={(e) => setDrafterIntent(e.target.value)}
              disabled={submitting}
              rows={4}
              placeholder={
                'Examples:\n'
                + '• Claim 3 element [104] must be clearly '
                + 'distinct from [102].\n'
                + '• Preserve the X-brace structure on the right.\n'
                + '• Ensure every reference numeral has a visible '
                + 'leader line to its element.'
              }
              className="w-full rounded-sm border border-zinc-300 bg-white px-2 py-1.5 text-[12px] text-zinc-800 focus:border-[#002FA7] focus:outline-none focus:ring-1 focus:ring-[#002FA7] disabled:opacity-50"
              data-testid="regenerate-drafter-intent"
              maxLength={1000}
            />
            <div className="mt-0.5 flex items-center justify-between">
              <p className="text-[10px] text-zinc-500">
                Frozen claims + drafter notes are passed
                verbatim to both tier-1 vision and tier-2 SVG
                generation. Be specific.
              </p>
              <span
                className={`text-[10px] tabular-nums ${
                  intentTooLong ? 'text-rose-600' : 'text-zinc-400'
                }`}
              >
                {intentLength}/1000
              </span>
            </div>
          </div>

          {/* Error */}
          {submitError && (
            <p
              className="rounded-sm bg-rose-50 px-2 py-1.5 text-[12px] text-rose-700 ring-1 ring-rose-200"
              data-testid="regenerate-error"
            >
              {submitError}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-zinc-200 px-5 py-3">
          <div>
            <p className="text-[11px] text-zinc-500">
              Generates 3 candidates. The original image stays
              preserved.
            </p>
            {style === 'match_source' && (
              <p
                className="text-[11px] text-zinc-500 mt-0.5"
                data-testid="regenerate-match-source-note"
              >
                Style: matching source — AI will preserve the
                original drawing style.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-sm border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
              data-testid="regenerate-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={submitting || intentTooLong}
              className="inline-flex items-center gap-1.5 rounded-sm bg-[#002FA7] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#001f7a] disabled:opacity-50"
              data-testid="regenerate-submit"
            >
              {submitting
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Sparkles className="h-3.5 w-3.5" />}
              Generate 3 candidates
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
